
import { CalculatorFormValues } from '../schema';
import { AssetCalculationResult } from './types';

export function calculateAssetContribution(data: CalculatorFormValues): AssetCalculationResult {
  const ASSET_CONTRIBUTION_RATE = data.applicantStatus === 'single' ? 0.075 : 0.0375;
  const ASSET_EXEMPTION = data.applicantStatus === 'single' ? 36000 : 72000;
  
  // Calculate cash assets (apply exemption to cash first)
  const totalCashAssets = data.savingsAndCash + data.investments + data.otherAssets;
  const exemptionAppliedToCash = Math.min(totalCashAssets, ASSET_EXEMPTION);
  const assessableCashAssets = Math.max(0, totalCashAssets - exemptionAppliedToCash);
  
  // Calculate remaining exemption for other assets
  const remainingExemption = Math.max(0, ASSET_EXEMPTION - exemptionAppliedToCash);
  
  // Calculate property asset values
  const primaryResidenceValue = data.ownsPrimaryResidence ? data.primaryResidenceValue : 0;
  const otherPropertiesValue = data.hasOtherProperties ? data.otherPropertiesValue : 0;
  const farmBusinessValue = data.hasFarmOrBusiness ? data.farmBusinessValue : 0;
  
  // Apply remaining exemption proportionally to non-cash assets
  const totalNonCashAssets = primaryResidenceValue + otherPropertiesValue + farmBusinessValue;
  
  let assessablePrimaryResidence = primaryResidenceValue;
  let assessableOtherProperties = otherPropertiesValue;
  let assessableFarmBusiness = farmBusinessValue;
  
  if (totalNonCashAssets > 0 && remainingExemption > 0) {
    const exemptionRatio = remainingExemption / totalNonCashAssets;
    assessablePrimaryResidence = Math.max(0, primaryResidenceValue * (1 - exemptionRatio));
    assessableOtherProperties = Math.max(0, otherPropertiesValue * (1 - exemptionRatio));
    assessableFarmBusiness = Math.max(0, farmBusinessValue * (1 - exemptionRatio));
  }
  
  // Calculate contributions
  const primaryResidenceContribution = assessablePrimaryResidence * ASSET_CONTRIBUTION_RATE;
  const otherPropertiesContribution = assessableOtherProperties * ASSET_CONTRIBUTION_RATE;
  const farmBusinessContribution = assessableFarmBusiness * ASSET_CONTRIBUTION_RATE;
  const cashAssetsContribution = assessableCashAssets * ASSET_CONTRIBUTION_RATE;
  
  return {
    primaryResidenceContribution,
    otherPropertiesContribution,
    farmBusinessContribution,
    cashAssetsContribution,
    totalAssetContribution: primaryResidenceContribution + otherPropertiesContribution + 
                           farmBusinessContribution + cashAssetsContribution
  };
}
